import { useMemo, useState } from 'react'
import {
  Badge,
  BadgeStatus,
  Button,
  Card,
  ConfirmDialog,
  DataTable,
  FilterBar,
  Input,
  KolomForm,
  Modal,
  PageHeader,
  Select,
  StatCard,
  Textarea,
  FILTER_KOSONG,
  useToast,
  type KolomTabel,
  type NilaiFilter,
} from '@/components'
import { api, buatIdempotencyKey, pesanKesalahan } from '@/lib/api'
import { formatRupiah, formatTanggal } from '@/lib/format'
import type { AkunKas, BarisMutasiBank, RekonsiliasiBank } from '@/lib/types'
import { useApi, usePaged, useAksi } from '@/lib/useApi'
import { useOpsiUnit } from '@/lib/opsi'
import { useAuth } from '@/lib/auth'

export default function RekonsiliasiPage() {
  const { punyaIzin } = useAuth()
  const opsiUnit = useOpsiUnit()
  const aksi = useAksi()
  const [filter, setFilter] = useState<NilaiFilter>({ ...FILTER_KOSONG, dari: `${new Date().getFullYear()}-01-01` })
  const [modalBuat, setModalBuat] = useState(false)
  const [detail, setDetail] = useState<RekonsiliasiBank | null>(null)
  const [konfirmasi, setKonfirmasi] = useState<{ tindakan: 'complete' | 'approve'; data: RekonsiliasiBank } | null>(null)

  const daftar = usePaged<RekonsiliasiBank>('/reconciliations', {
    query: {
      dari: filter.dari || undefined,
      sampai: filter.sampai || undefined,
      unitId: filter.unitId || undefined,
    },
  })

  const kolom = useMemo<KolomTabel<RekonsiliasiBank>[]>(
    () => [
      {
        kunci: 'cashAccount',
        judul: 'Akun Kas/Bank',
        render: (b) => (
          <button type="button" className="font-medium text-yayasan-800 hover:underline" onClick={() => setDetail(b)}>
            {b.cashAccount?.name ?? b.cashAccountId}
          </button>
        ),
      },
      { kunci: 'statementDate', judul: 'Tanggal Rekening', render: (b) => formatTanggal(b.statementDate) },
      { kunci: 'statementBalance', judul: 'Saldo Rekening', rata: 'kanan', render: (b) => formatRupiah(b.statementBalance) },
      { kunci: 'bookBalance', judul: 'Saldo Buku', rata: 'kanan', render: (b) => formatRupiah(b.bookBalance) },
      {
        kunci: 'difference',
        judul: 'Selisih',
        rata: 'kanan',
        render: (b) => (
          <span className={b.difference !== 0 ? 'font-medium text-red-700' : 'font-medium text-yayasan-800'}>
            {formatRupiah(b.difference)}
          </span>
        ),
      },
      {
        kunci: 'matchedCount',
        judul: 'Cocok / Tidak',
        rata: 'kanan',
        render: (b) => (
          <span>
            <Badge nada="sukses">{b.matchedCount}</Badge>{' '}
            <Badge nada={b.unmatchedCount > 0 ? 'peringatan' : 'netral'}>{b.unmatchedCount}</Badge>
          </span>
        ),
      },
      { kunci: 'status', judul: 'Status', render: (b) => <BadgeStatus status={b.status} /> },
      {
        kunci: 'aksi',
        judul: 'Aksi',
        sortable: false,
        render: (b) => (
          <div className="flex flex-wrap gap-1.5">
            <Button ukuran="kecil" varian="garis" onClick={() => setDetail(b)}>
              Pencocokan
            </Button>
            {punyaIzin('reconciliation:create') && b.status === 'DRAFT' ? (
              <Button ukuran="kecil" onClick={() => setKonfirmasi({ tindakan: 'complete', data: b })}>
                Selesaikan
              </Button>
            ) : null}
            {punyaIzin('reconciliation:approve') && b.status === 'COMPLETED' ? (
              <Button ukuran="kecil" varian="sekunder" onClick={() => setKonfirmasi({ tindakan: 'approve', data: b })}>
                Setujui
              </Button>
            ) : null}
          </div>
        ),
      },
    ],
    [punyaIzin],
  )

  return (
    <div>
      <PageHeader
        judul="Rekonsiliasi Bank"
        deskripsi="Mencocokkan mutasi rekening bank dengan catatan buku besar, menghitung selisih, lalu menutup rekonsiliasi."
        ekspor={{
          path: '/reports/bank-book',
          namaDasar: 'rekonsiliasi-bank',
          query: { dari: filter.dari || undefined, sampai: filter.sampai || undefined },
        }}
        aksi={
          punyaIzin('reconciliation:create') ? <Button onClick={() => setModalBuat(true)}>+ Rekonsiliasi Baru</Button> : null
        }
      />

      <FilterBar
        nilai={filter}
        onUbah={setFilter}
        unit={opsiUnit}
        tampilkan={{ unitId: true, dari: true, sampai: true, fundId: false, program: false, kategori: false }}
      />

      <DataTable
        kolom={kolom}
        data={daftar.items}
        kunciBaris={(b) => b.id}
        memuat={daftar.memuat}
        galat={daftar.galat}
        kodeGalat={daftar.kodeGalat}
        onCoba={daftar.muat}
        meta={daftar.meta}
        onHalaman={daftar.setHalaman}
        pesanKosong="Belum ada rekonsiliasi bank."
      />

      <FormRekonsiliasi terbuka={modalBuat} onTutup={() => setModalBuat(false)} setelah={daftar.muat} />
      <DetailRekonsiliasi
        data={detail}
        onTutup={() => setDetail(null)}
        setelah={daftar.muat}
        punyaIzin={punyaIzin}
      />

      <ConfirmDialog
        terbuka={konfirmasi !== null}
        onTutup={() => setKonfirmasi(null)}
        judul={konfirmasi?.tindakan === 'approve' ? 'Setujui Rekonsiliasi' : 'Selesaikan Rekonsiliasi'}
        pesan={
          <>
            Rekonsiliasi {konfirmasi?.data.cashAccount?.name ?? ''} tanggal{' '}
            {formatTanggal(konfirmasi?.data.statementDate ?? null)} dengan selisih{' '}
            <strong>{formatRupiah(konfirmasi?.data.difference ?? 0)}</strong> akan{' '}
            {konfirmasi?.tindakan === 'approve' ? 'disetujui' : 'ditandai selesai'}.
          </>
        }
        labelKonfirmasi="Lanjutkan"
        berbahaya={false}
        memuat={aksi.memuat}
        onKonfirmasi={async () => {
          if (!konfirmasi) return
          const hasil = await aksi.jalankan(
            () =>
              api.post(`/reconciliations/${konfirmasi.data.id}/${konfirmasi.tindakan}`, {}, {
                idempotencyKey: buatIdempotencyKey(),
              }),
            { sukses: 'Rekonsiliasi berhasil diproses.' },
          )
          if (hasil !== null) {
            setKonfirmasi(null)
            daftar.muat()
          }
        }}
      />
    </div>
  )
}

function FormRekonsiliasi({
  terbuka,
  onTutup,
  setelah,
}: {
  terbuka: boolean
  onTutup: () => void
  setelah: () => void
}) {
  const opsiUnit = useOpsiUnit()
  const kas = useApi<unknown>(terbuka ? '/cash-accounts' : null, { perPage: 300 })
  const daftarKas: AkunKas[] = Array.isArray(kas.data)
    ? (kas.data as AkunKas[])
    : ((kas.data as { items?: AkunKas[] } | null)?.items ?? [])

  const [cashAccountId, setCashAccountId] = useState('')
  const [statementDate, setStatementDate] = useState(new Date().toISOString().slice(0, 10))
  const [statementBalance, setStatementBalance] = useState('')
  const [bookBalance, setBookBalance] = useState('')
  const [notes, setNotes] = useState('')
  const [galat, setGalat] = useState<string | null>(null)
  const [memuat, setMemuat] = useState(false)

  const selisih =
    (Number(statementBalance.replace(/[^0-9.-]/g, '')) || 0) - (Number(bookBalance.replace(/[^0-9.-]/g, '')) || 0)

  const simpan = async () => {
    setGalat(null)
    if (!cashAccountId) return setGalat('Akun kas/bank wajib dipilih.')
    if (!statementBalance || !bookBalance) return setGalat('Saldo rekening dan saldo buku wajib diisi.')
    setMemuat(true)
    try {
      await api.post(
        '/reconciliations',
        {
          cashAccountId,
          statementDate,
          statementBalance: Number(statementBalance.replace(/[^0-9.-]/g, '')),
          bookBalance: Number(bookBalance.replace(/[^0-9.-]/g, '')),
          notes: notes.trim() || undefined,
        },
        { idempotencyKey: buatIdempotencyKey() },
      )
      setelah()
      onTutup()
    } catch (e) {
      setGalat(pesanKesalahan(e))
    } finally {
      setMemuat(false)
    }
  }

  return (
    <Modal
      terbuka={terbuka}
      onTutup={onTutup}
      judul="Rekonsiliasi Bank Baru"
      lebar="max-w-2xl"
      footer={
        <>
          <Button varian="garis" onClick={onTutup} disabled={memuat}>
            Batal
          </Button>
          <Button onClick={() => void simpan()} memuat={memuat}>
            Simpan
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {galat ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {galat}
          </div>
        ) : null}
        <KolomForm kolom={2}>
          <Select
            label="Akun Kas/Bank"
            placeholder={kas.memuat ? 'Memuat…' : 'Pilih akun'}
            opsi={daftarKas.map((k) => ({ value: k.id, label: `${k.code} — ${k.name}` }))}
            value={cashAccountId}
            onChange={(e) => setCashAccountId(e.target.value)}
            required
            bantuan={kas.galat ? `Gagal memuat akun: ${kas.galat}` : undefined}
          />
          <Input
            type="date"
            label="Tanggal Rekening Koran"
            value={statementDate}
            onChange={(e) => setStatementDate(e.target.value)}
            required
          />
          <Input
            label="Saldo Menurut Rekening"
            inputMode="numeric"
            value={statementBalance}
            onChange={(e) => setStatementBalance(e.target.value)}
            required
          />
          <Input
            label="Saldo Menurut Buku"
            inputMode="numeric"
            value={bookBalance}
            onChange={(e) => setBookBalance(e.target.value)}
            required
          />
        </KolomForm>
        <p className="text-sm text-netral-600">
          Selisih awal: <strong className="angka-tabular">{formatRupiah(selisih)}</strong>
        </p>
        <Textarea label="Catatan" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
      </div>
    </Modal>
  )
}

function DetailRekonsiliasi({
  data,
  onTutup,
  setelah,
  punyaIzin,
}: {
  data: RekonsiliasiBank | null
  onTutup: () => void
  setelah: () => void
  punyaIzin: (...kode: string[]) => boolean
}) {
  const toast = useToast()
  const detail = useApi<RekonsiliasiBank>(data ? `/reconciliations/${data.id}` : null)
  const isi = detail.data ?? data
  const [mengunggah, setMengunggah] = useState(false)

  const baris = isi?.lines ?? []
  const cocok = baris.filter((b) => b.matchStatus === 'MATCHED').length
  const belum = baris.filter((b) => b.matchStatus !== 'MATCHED').length
  const totalRekening = baris.reduce((a, b) => a + (b.debit ?? 0) - (b.credit ?? 0), 0)
  const selisih = (isi?.statementBalance ?? 0) - (isi?.bookBalance ?? 0)

  const kolom: KolomTabel<BarisMutasiBank>[] = [
    { kunci: 'lineNumber', judul: '#', lebar: '56px' },
    { kunci: 'statementDate', judul: 'Tanggal', render: (b) => formatTanggal(b.statementDate) },
    { kunci: 'description', judul: 'Uraian', render: (b) => b.description ?? '—' },
    { kunci: 'reference', judul: 'Referensi', render: (b) => b.reference ?? '—' },
    { kunci: 'debit', judul: 'Debit', rata: 'kanan', render: (b) => (b.debit ? formatRupiah(b.debit) : '—') },
    { kunci: 'credit', judul: 'Kredit', rata: 'kanan', render: (b) => (b.credit ? formatRupiah(b.credit) : '—') },
    { kunci: 'matchedAmount', judul: 'Nilai Cocok', rata: 'kanan', render: (b) => (b.matchedAmount ? formatRupiah(b.matchedAmount) : '—') },
    {
      kunci: 'matchStatus',
      judul: 'Status Pencocokan',
      render: (b) => (
        <Badge nada={b.matchStatus === 'MATCHED' ? 'sukses' : b.matchStatus === 'UNMATCHED' ? 'peringatan' : 'info'}>
          {b.matchStatus}
        </Badge>
      ),
    },
    {
      kunci: 'aksi',
      judul: 'Aksi',
      sortable: false,
      render: (b) =>
        b.matchStatus !== 'MATCHED' && punyaIzin('reconciliation:create') ? (
          <Button
            ukuran="kecil"
            varian="garis"
            onClick={async () => {
              if (!isi) return
              try {
                await api.post(`/reconciliations/${isi.id}/match`, { lineId: b.id, matchType: 'MANUAL' })
                toast.sukses('Baris berhasil dicocokkan.')
                detail.muat()
              } catch (e) {
                toast.gagal(pesanKesalahan(e))
              }
            }}
          >
            Cocokkan
          </Button>
        ) : (
          <span className="text-xs text-netral-400">—</span>
        ),
    },
  ]

  return (
    <Modal
      terbuka={data !== null}
      onTutup={onTutup}
      judul={isi?.cashAccount?.name ? `Rekonsiliasi ${isi.cashAccount.name}` : 'Detail Rekonsiliasi'}
      deskripsi="Unggah mutasi (CSV/XLSX) lalu cocokkan setiap baris dengan catatan buku."
      lebar="max-w-6xl"
      footer={
        <>
          {punyaIzin('reconciliation:create') ? (
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-netral-300 bg-white px-3 py-2 text-sm text-netral-700 hover:bg-netral-100">
              {mengunggah ? 'Mengunggah…' : 'Unggah Mutasi CSV/XLSX'}
              <input
                type="file"
                className="hidden"
                accept=".csv,.xlsx,.xls,text/csv"
                onChange={async (e) => {
                  const berkas = e.target.files?.[0]
                  if (!berkas || !isi) return
                  setMengunggah(true)
                  try {
                    const fd = new FormData()
                    fd.append('file', berkas)
                    await api.unggah(`/reconciliations/${isi.id}/import`, fd)
                    toast.sukses('Mutasi rekening berhasil diimpor.')
                    detail.muat()
                    setelah()
                  } catch (err) {
                    toast.gagal(pesanKesalahan(err))
                  } finally {
                    setMengunggah(false)
                  }
                }}
              />
            </label>
          ) : null}
          <Button varian="garis" onClick={detail.muat}>
            Muat ulang
          </Button>
          <Button varian="halus" onClick={onTutup}>
            Tutup
          </Button>
        </>
      }
    >
      {detail.galat ? (
        <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{detail.galat}</p>
      ) : null}

      {isi ? (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard label="Saldo Rekening" nilai={isi.statementBalance} formatNilai={(v) => formatRupiah(Number(v ?? 0))} />
            <StatCard label="Saldo Buku" nilai={isi.bookBalance} formatNilai={(v) => formatRupiah(Number(v ?? 0))} />
            <StatCard
              label="Selisih"
              nilai={selisih}
              nada={selisih === 0 ? 'hijau' : 'merah'}
              formatNilai={(v) => formatRupiah(Number(v ?? 0))}
            />
            <StatCard
              label="Baris Cocok / Belum"
              nilai={`${cocok} / ${belum}`}
              sublabel={`Total mutasi rekening ${formatRupiah(totalRekening)}`}
            />
          </div>

          <Card padat className="overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-netral-200 px-4 py-2.5">
              <h3 className="text-sm font-semibold text-netral-800">Tabel Pencocokan</h3>
              <div className="flex items-center gap-2 text-xs">
                <Badge nada="sukses">MATCHED {cocok}</Badge>
                <Badge nada="peringatan">UNMATCHED {belum}</Badge>
              </div>
            </div>
            <DataTable
              kolom={kolom}
              data={baris}
              kunciBaris={(b) => b.id}
              memuat={detail.memuat && baris.length === 0}
              pesanKosong="Belum ada mutasi rekening. Unggah berkas CSV/XLSX terlebih dahulu."
            />
          </Card>
        </div>
      ) : null}
    </Modal>
  )
}
